import { createSlice } from "@reduxjs/toolkit";
import { ProjectsWorkOrders } from "../../../SharedModule/types/shared-module-types";
import {
  EntryListView,
  MealBreakDay,
  TimeEntryForm,
} from "../../types/times-module-types";
import {
  getEmptyTimeEntry,
  getProjectWorkOrder,
  getValueFromSelect,
} from "../../utils/timesUtils";
import { newUuid } from "../../../SharedModule/utils/uuid";
import { isSameDay } from "../../../SharedModule/utils/dateUtils";
import { billableIds } from "../../../SharedModule/utils/constants";
import { decodeURIComponentSafe } from "../../../SharedModule/utils/formatters";
import {
  computeEntryListAfterLoad,
  computeTimeEntriesSheetViewWithFixups,
} from "./timesReducerHelpers";

type TimesState = {
  hasPageError: boolean;
  projectList: ProjectsWorkOrders[];
  isAdmin: boolean;
  isListView: boolean;
  user: string;
  timeEntriesList: EntryListView[];
  timeEntriesSheetView: any[];
  summary: {
    total: string;
    billable: string;
    nonBillable: string;
    internal: string;
    timeOff: string;
  };
  entryList: TimeEntryForm[];
  entryListInvalid: TimeEntryForm[];
  isLoading: boolean;
  // Tracks only the summary header.  Set false when the fast /summary
  // aggregation resolves (summaryChain) so the cards stop showing
  // skeletons independently of the slow full-entries load.
  isSummaryLoading: boolean;
  // True while re-fetching after first load (week navigation).
  // Unlike isLoading, this leaves controls enabled so the user can
  // navigate away; skeleton rows still show so the list isn't blank.
  isRefreshing: boolean;
  // Flipped to true after the first successful loadTimesInfo so the
  // hook knows to use isRefreshing (stale-while-revalidate) on
  // subsequent navigations instead of the full isLoading spinner.
  hasInitiallyLoaded: boolean;
  isSubmitted: boolean;
  holidays: string[];
  // Tracks which user/date-range the current `holidays` cache covers, so
  // loadHolidaysIfNeeded (useTimes.tsx) can skip re-fetching on plain week
  // navigation and only re-fetch on user change or when the viewed date
  // falls outside the cached range (e.g. jumping via the date picker).
  holidaysUserId: string | null;
  holidaysRange: { start: string; end: string } | null;
  scheduledHours: number;
  missingTime: {
    missingTime: boolean;
    hours: number;
    startDate: string;
    endDate: string;
  };
  // Tracks which user the current `missingTime` value belongs to. The
  // underlying calculation (MissingTimeService.GetMissingTime) doesn't
  // depend on the viewed week at all, so remindersChain (useTimes.tsx)
  // skips re-fetching on plain week navigation when this already matches
  // the active user, and only forces a fresh fetch right after the user
  // adds/edits/deletes a time entry (via refreshPage).
  missingTimeUserId: string | null;
  missingTimeMealBreak: MealBreakDay[];
};

const initialState: TimesState = {
  hasPageError: false,
  projectList: [],
  isAdmin: false,
  isListView: true,
  user: "",
  timeEntriesList: [],
  timeEntriesSheetView: [],
  summary: {
    total: "—",
    billable: "—",
    nonBillable: "—",
    internal: "—",
    timeOff: "—",
  },
  entryList: [
    {
      ...getEmptyTimeEntry(new Date()),
      entryId: getEmptyTimeEntry(new Date()).entryId,
    },
    {
      ...getEmptyTimeEntry(new Date()),
      entryId: getEmptyTimeEntry(new Date()).entryId,
    },
    {
      ...getEmptyTimeEntry(new Date()),
      entryId: getEmptyTimeEntry(new Date()).entryId,
    },
    {
      ...getEmptyTimeEntry(new Date()),
      entryId: getEmptyTimeEntry(new Date()).entryId,
    },
    {
      ...getEmptyTimeEntry(new Date()),
      entryId: getEmptyTimeEntry(new Date()).entryId,
    },
  ],
  entryListInvalid: [],
  isLoading: true,
  isSummaryLoading: true,
  isRefreshing: false,
  hasInitiallyLoaded: false,
  isSubmitted: false,
  holidays: [],
  holidaysUserId: null,
  holidaysRange: null,
  scheduledHours: 8,
  missingTime: {
    missingTime: false,
    hours: 0,
    startDate: "",
    endDate: "",
  },
  missingTimeUserId: null,
  missingTimeMealBreak: [],
};

export const timesSlice = createSlice({
  name: "times",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
      if (action.payload === true) {
        // Full load: reset both skeleton flags.
        state.isSummaryLoading = true;
        state.isRefreshing = false;
      }
    },
    // Stale-while-revalidate: used on subsequent week navigations so controls
    // stay enabled while new data loads behind the current (stale) view.
    setRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
      if (action.payload === true) {
        state.isSummaryLoading = true; // summary skeleton resets too
      }
    },
    setSubmitted: (state, action) => {
      state.isSubmitted = action.payload;
    },
    // Updates only the project/work-order dropdown list — called independently
    // of loadTimesInfo so the project picker can populate without blocking the
    // entries + summary render.
    setProjectList: (state, action) => {
      state.projectList = action.payload;
    },
    // Only dispatched by entriesChain on success now — on failure the chain
    // dispatches setHasPageError(true) directly and skips this action, since
    // the response no longer carries user/summary (those live in /summary).
    loadTimesInfo: (state, action) => {
      // undefined means summaryChain (updateSummaryOnly) already wrote these —
      // preserve them. null/value means explicitly set or no data available.
      if (action.payload.missingTime !== undefined) {
        state.missingTime = action.payload.missingTime
          ? action.payload.missingTime.data
          : initialState.missingTime;
      }
      if (action.payload.missingTimeMealBreak !== undefined) {
        state.missingTimeMealBreak = action.payload.missingTimeMealBreak
          ? action.payload.missingTimeMealBreak.data
          : initialState.missingTimeMealBreak;
      }
      // projectList is optional — omit it when using the parallel loading path
      // where setProjectList handles it independently.
      if (action.payload.projectList !== undefined) {
        state.projectList = action.payload.projectList;
      }

      // if has content in same day or has invalid entries, clean entry list
      state.timeEntriesList = action.payload.timeEntriesInfo.data.listView;
      let hasPageContent: boolean =
        state.timeEntriesList.length > 0 &&
        state.timeEntriesList.some((entry) =>
          isSameDay(entry.entryDate, action.payload.pageDate)
        );

      const entryListResult = computeEntryListAfterLoad(
        state.entryList,
        state.entryListInvalid,
        hasPageContent,
        action.payload.pageDate
      );
      state.entryList = entryListResult.entryList;
      state.entryListInvalid = entryListResult.entryListInvalid;
      if (entryListResult.isSubmitted) {
        state.isSubmitted = true;
      }

      state.timeEntriesSheetView = computeTimeEntriesSheetViewWithFixups(
        action.payload.timeEntriesInfo.data.sheetView
      );

      state.isLoading = false;
      state.isRefreshing = false;
      state.hasInitiallyLoaded = true;
      // Fallback: clear summary skeleton even if summaryChain never fired.
      state.isSummaryLoading = false;
    },
    // Reset to false once per load cycle (start of loadTimesPageInfo), and
    // set to true by whichever chain (summaryChain or entriesChain) fails —
    // neither chain's success path resets it back to false, so one chain
    // failing can't be silently masked by the other chain succeeding later.
    setHasPageError: (state, action) => {
      state.hasPageError = action.payload;
    },
    // Pre-populates summary totals, scheduledHours, and holidays from the fast
    // /summary aggregation endpoint WITHOUT touching isLoading, timeEntriesList,
    // entryList, or timeEntriesSheetView.
    // Setting isSummaryLoading=false here is the key change: it clears the
    // skeleton bars on the summary cards as soon as the fast path resolves
    // (~100 ms), long before the full entries endpoint finishes.
    // Reminder data (missingTime / missingTimeMealBreak) is handled
    // exclusively by loadTimesInfo once entriesChain resolves.
    updateSummaryOnly: (state, action) => {
      state.summary = action.payload.summary;
      state.scheduledHours = action.payload.user.scheduledHours;
      state.isSummaryLoading = false;
    },
    // Populated by loadHolidaysIfNeeded (useTimes.tsx) from the dedicated
    // /holidays endpoint — fetched once per user/date-range instead of on
    // every week navigation. See holidaysUserId/holidaysRange above.
    setHolidays: (state, action) => {
      state.holidays = action.payload.holidays;
      state.holidaysUserId = action.payload.userId;
      state.holidaysRange = action.payload.range;
    },
    changeListView: (state, action) => {
      state.isListView = action.payload;
    },
    updateEntry: (state, action) => {
      let foundIndex = state.entryList.findIndex(
        (x) => x.entryId === action.payload.entryState.entryId
      );
      state.entryList[foundIndex] = action.payload.entryState;
    },
    addInvalidEntriesFromEndpoint: (state, action) => {
      let aux: TimeEntryForm;

      action.payload.list.forEach((element) => {
        aux = {
          entryId: newUuid(),
          entryCanEdit: true,
          entryCanDelete: true,
          entryDate: element.timeEntry.entryDate,
          entryDateValid: true,
          entryProjectWorkOrder: getProjectWorkOrder(
            state.projectList,
            element.timeEntry.entityId
          ),
          entryProjectWorkOrderValid: true,
          entryHours: element.timeEntry.hours,
          entryHoursValid: true,
          entryTaskType: getValueFromSelect(
            action.payload.meta.taskTypes,
            element.timeEntry.taskTypeId
          ),
          entryTaskTypeValid: true,
          entryDescription: decodeURIComponentSafe(
            element.timeEntry.description
          ),
          entryIsBillable: billableIds.includes(
            element.timeEntry.timeEntryTypeId.toString().toLowerCase()
          ),
          entryNonBillableReason: billableIds.includes(
            element.timeEntry.timeEntryTypeId.toString().toLowerCase()
          )
            ? null
            : getValueFromSelect(
                action.payload.meta.nonBillableReasons,
                element.timeEntry.nonBillableReasonId
              ),
          entryNonBillableReasonValid: true,
          // ASUITE11-2700: set valid true if price error, to show again message
          isEntryValid: action.payload.isEntryValid,
          isEntryTouched: true,
        };
        state.entryListInvalid.push(aux);
      });
    },
    addEntry: (state, action) => {
      state.entryList.unshift({
        ...getEmptyTimeEntry(action.payload),
        entryId: getEmptyTimeEntry(action.payload).entryId,
      });
    },
    addEntryAtBottom: (state, action) => {
      state.entryList.push({
        ...getEmptyTimeEntry(action.payload),
        entryId: getEmptyTimeEntry(action.payload).entryId,
      });
    },
    addInvalidEntry: (state, action) => {
      state.entryListInvalid.unshift(action.payload.entry);
    },
    removeValidEntry: (state, action) => {
      state.entryList = state.entryList.filter(
        (entry) => entry.entryId !== action.payload.entry.entryId
      );
    },
    resetEntries: (state, action) => {
      if (action.payload.hasContent) {
        state.entryList = [];
      } else {
        state.entryList = [
          {
            ...getEmptyTimeEntry(action.payload.pageDate),
            entryId: getEmptyTimeEntry(action.payload.pageDate).entryId,
          },
          {
            ...getEmptyTimeEntry(action.payload.pageDate),
            entryId: getEmptyTimeEntry(action.payload.pageDate).entryId,
          },
          {
            ...getEmptyTimeEntry(action.payload.pageDate),
            entryId: getEmptyTimeEntry(action.payload.pageDate).entryId,
          },
          {
            ...getEmptyTimeEntry(action.payload.pageDate),
            entryId: getEmptyTimeEntry(action.payload.pageDate).entryId,
          },
          {
            ...getEmptyTimeEntry(action.payload.pageDate),
            entryId: getEmptyTimeEntry(action.payload.pageDate).entryId,
          },
        ];
      }
    },
    // Stores the result of the parallel remindersChain — called independently
    // of loadTimesInfo so reminder banners can paint as soon as the fast
    // /summary endpoint resolves (~100 ms) rather than waiting for the slow
    // full-entries endpoint (~3 s).
    setReminderData: (state, action) => {
      if (action.payload.missingTime !== undefined) {
        state.missingTime = action.payload.missingTime
          ? action.payload.missingTime.data
          : initialState.missingTime;
        // Only updated when we actually made a decision (fetched fresh or
        // explicitly turned off) — a cache-skip omits missingTime entirely
        // (stays undefined) so this key is left untouched in that case.
        state.missingTimeUserId = action.payload.userId ?? null;
      }
      if (action.payload.missingTimeMealBreak !== undefined) {
        state.missingTimeMealBreak = action.payload.missingTimeMealBreak
          ? action.payload.missingTimeMealBreak.data
          : initialState.missingTimeMealBreak;
      }
    },
    resetInvalidEntries: (state) => {
      state.entryListInvalid = [];
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setLoading,
  setRefreshing,
  setSubmitted,
  setProjectList,
  loadTimesInfo,
  updateSummaryOnly,
  setHolidays,
  setHasPageError,
  setReminderData,
  changeListView,
  updateEntry,
  addInvalidEntriesFromEndpoint,
  addEntry,
  addEntryAtBottom,
  addInvalidEntry,
  removeValidEntry,
  resetEntries,
  resetInvalidEntries,
} = timesSlice.actions;

// Selectors
export const IsLoading = (state: any) => {
  return state.times.isLoading;
};

export const IsSummaryLoading = (state: any) => {
  return state.times.isSummaryLoading;
};

export const IsRefreshing = (state: any) => {
  return state.times.isRefreshing;
};

export const HasInitiallyLoaded = (state: any) => {
  return state.times.hasInitiallyLoaded;
};

export const HasPageError = (state: any) => {
  return state.times.hasPageError;
};

export const IsSubmitted = (state: any) => {
  return state.times.isSubmitted;
};

export const IsListView = (state: any) => {
  return state.times.isListView;
};

export const ProjectList = (state: any) => {
  return state.times.projectList;
};

export const MissingTime = (state: any) => {
  return state.times.missingTime;
};

export const MissingTimeUserId = (state: any) => {
  return state.times.missingTimeUserId;
};

export const MissingTimeMealBreak = (state: any) => {
  return state.times.missingTimeMealBreak;
};

export const ScheduledHours = (state: any) => {
  return state.times.scheduledHours;
};

// To load select grouped by
export const SelectProjectOrWorkOrder = (state: any) => {
  return [
    {
      label: "Work Orders",
      options: state.times.projectList?.filter((item) => {
        if (item.categoryId === 3) {
          return item;
        }
      }),
    },
    {
      label: "Projects",
      options: state.times.projectList?.filter((item) => {
        if (item.categoryId !== 3) {
          return item;
        }
      }),
    },
  ];
};

export const Summary = (state: any) => {
  return state.times.summary;
};

export const TimeEntriesList = (state: any) => {
  return state.times.timeEntriesList;
};

export const TimeEntriesSheetView = (state: any) => {
  return state.times.timeEntriesSheetView;
};

export const Holidays = (state: any) => {
  return state.times.holidays;
};

export const HolidaysUserId = (state: any) => {
  return state.times.holidaysUserId;
};

export const HolidaysRange = (state: any) => {
  return state.times.holidaysRange;
};

export const EntryList = (state: any) => {
  return state.times.entryList;
};

export default timesSlice.reducer;
